import React, { useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Paper, Typography, TextField, Button, Avatar, Stack, Divider, Chip,
} from '@mui/material'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import DeleteIcon from '@mui/icons-material/Delete'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'

Show.layout = (page) => <AppShell>{page}</AppShell>

const ROLE_LABELS = {
  admin: 'Administrator',
  manager: 'Manager',
  accountant: 'Buchhalter',
  readonly: 'Nur Lesen',
}

export default function Show({ profile }) {
  const { props } = usePage()
  const tenantSlug = props.tenant?.slug

  const [firstName, setFirstName] = useState(profile.user.first_name || '')
  const [lastName, setLastName] = useState(profile.user.last_name || '')
  const [phone, setPhone] = useState(profile.tenant_user.phone || '')
  const [position, setPosition] = useState(profile.tenant_user.position || '')
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(profile.user.avatar_url || null)
  const [removeAvatar, setRemoveAvatar] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef(null)

  const initials = (profile.user.first_name?.[0] || profile.user.email?.[0] || 'U').toUpperCase()

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarFile(file)
    setRemoveAvatar(false)
    const reader = new FileReader()
    reader.onload = (ev) => setAvatarPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleRemoveAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
    setRemoveAvatar(true)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitting(true)
    const formData = new FormData()
    formData.append('user[first_name]', firstName)
    formData.append('user[last_name]', lastName)
    if (avatarFile) formData.append('user[avatar]', avatarFile)
    if (removeAvatar) formData.append('user[remove_avatar]', '1')
    formData.append('tenant_user[phone]', phone)
    formData.append('tenant_user[position]', position)

    router.post(`/m/${tenantSlug}/profile`, formData, {
      forceFormData: true,
      headers: { 'X-HTTP-Method-Override': 'PATCH' },
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Box>
      <PageHeader
        title="Profil"
        breadcrumbs={[
          { label: 'Dashboard', href: `/m/${tenantSlug}` },
          { label: 'Profil' },
        ]}
      />

      <Box component="form" onSubmit={handleSubmit} noValidate data-testid="profile-form">
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Persönliche Daten
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Diese Angaben gelten für alle Mandanten.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="flex-start" sx={{ mb: 3 }}>
              <Stack alignItems="center" spacing={1} sx={{ minWidth: 140 }}>
                <Avatar
                  src={avatarPreview || undefined}
                  sx={{ width: 96, height: 96, fontSize: '2rem', bgcolor: 'primary.main', alignSelf: 'center', mx: 'auto' }}
                  data-testid="avatar-preview"
                >
                  {!avatarPreview && initials}
                </Avatar>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                  data-testid="avatar-upload-btn"
                >
                  Hochladen
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={handleAvatarChange}
                    data-testid="avatar-file-input"
                  />
                </Button>
                {avatarPreview && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveAvatar}
                    data-testid="avatar-remove-btn"
                  >
                    Entfernen
                  </Button>
                )}
              </Stack>

              <Stack spacing={2} sx={{ flex: 1 }}>
                <TextField
                  label="Vorname"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { 'data-testid': 'first-name-input' } }}
                />
                <TextField
                  label="Nachname"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  fullWidth
                  slotProps={{ htmlInput: { 'data-testid': 'last-name-input' } }}
                />
                <TextField
                  label="E-Mail"
                  value={profile.user.email}
                  fullWidth
                  disabled
                  helperText="E-Mail kann derzeit nicht geändert werden."
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Mandantenspezifische Angaben
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Mandant:
              </Typography>
              <Chip label={profile.tenant.name} size="small" />
              <Typography variant="body2" color="text.secondary">
                Rolle:
              </Typography>
              <Chip label={ROLE_LABELS[profile.tenant_user.role] || profile.tenant_user.role} size="small" color="primary" />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <TextField
                label="Telefonnummer"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
                slotProps={{ htmlInput: { 'data-testid': 'phone-input', maxLength: 50 } }}
              />
              <TextField
                label="Position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="z.B. Hausverwalter:in, Assistenz"
                fullWidth
                slotProps={{ htmlInput: { 'data-testid': 'position-input', maxLength: 100 } }}
              />
            </Stack>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              data-testid="profile-submit-btn"
            >
              Speichern
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}
